// The /apply funnel's hooks (.design-specs/apply.plan.md) — the WRAPPER layer for the anonymous form. Nothing here
// is tenant data: the applicant has no account and no band, so the keys carry no tenant and the routes are all
// under /api/v1/public/applications. Every call is a plain openapi-fetch request through the app client.
import { useMutation, useQuery } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";
import { fetchMe } from "../auth/session.js";

export type ApplyConfig = components["schemas"]["ApplyConfig"];
export type ApplyTitleOption = components["schemas"]["ApplyTitleOption"];
export type ApplySpotifyArtist = components["schemas"]["ApplySpotifyArtist"];
export type ApplyInstagramDraft = components["schemas"]["ApplyInstagramDraft"];
export type SubmitApplicationRequest = components["schemas"]["SubmitApplicationRequest"];

/** The form's server inputs: the Turnstile site key, the contact address, the band titles for "Your role". */
export function useApplyConfig() {
  const { api } = useApp();
  return useQuery({
    queryKey: ["public", "apply", "config"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/public/applications/config");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** The artist a pasted Spotify link names. Fired on blur, not per keystroke. */
export function useApplySpotifyLookup() {
  const { api } = useApp();
  return useMutation({
    mutationFn: async (link: string) => {
      const { data, error, response } = await api.GET("/api/v1/public/applications/spotify-artist", {
        params: { query: { link } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

/** Begin the Instagram verification: the Meta authorize URL for the popup. */
export function useApplyInstagramStart() {
  const { api } = useApp();
  return useMutation({
    mutationFn: async (args: { spotifyLink: string; returnUrl: string }) => {
      const { data, error, response } = await api.POST("/api/v1/public/applications/instagram/start", {
        params: { query: { returnUrl: args.returnUrl } },
        body: { spotifyLink: args.spotifyLink },
      });
      if (!response.ok || !data?.authorizeUrl) throw new ApiError(response.status, error);
      return data.authorizeUrl;
    },
  });
}

/** Read a finished connect by the draft token the popup handed back. */
export function useApplyInstagramDraft() {
  const { api } = useApp();
  return useMutation({
    mutationFn: async (draft: string) => {
      const { data, error, response } = await api.POST("/api/v1/public/applications/instagram/draft", {
        body: { draft },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

/** Submit the application. */
export function useSubmitApplication() {
  const { api } = useApp();
  return useMutation({
    mutationFn: async (body: SubmitApplicationRequest) => {
      const { data, error, response } = await api.POST("/api/v1/public/applications", { body });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

/**
 * The signed-in account applying (ART-307): `GET /me`, under the same key the app uses, but only while there is
 * a session — the page is open signed out too, where asking would only earn a 401. The server never trusts this:
 * it reads the applicant off the request's own session.
 */
export function useApplicantAccount(signedIn: boolean) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["me"],
    queryFn: () => fetchMe(api),
    enabled: signedIn,
    staleTime: 60_000,
    retry: false,
  });
}
